/**
 * Per-page title, description and canonical URL.
 *
 * `index.html` carries one set of tags for the whole SPA, and CloudFront serves
 * that same shell at every route — including a hardcoded
 * `<link rel="canonical" href="https://svcdue.com/">`. For the app itself that
 * is right: everything behind sign-in is deliberately not indexed (robots.txt),
 * so pointing a crawler back at the front door is exactly what we want.
 *
 * It is wrong for a public page that is *meant* to be found. A canonical tag
 * saying "the real version of this is the home page" is an instruction not to
 * index it, so listing such a page in sitemap.xml while the shell's canonical
 * still points at `/` is self-cancelling. This rewrites the three tags that
 * matter while such a page is mounted, and puts them back on the way out.
 *
 * It only reaches crawlers that execute JavaScript. Google does; not everything
 * does. Pre-rendering would be the thorough fix, and is a bigger change than one
 * page is worth today.
 */

import { useEffect } from 'react';

export const SITE_ORIGIN = 'https://svcdue.com';

function setMeta(selector: string, attribute: 'content' | 'href', value: string): () => void {
  const node = document.head.querySelector(selector);
  if (!node) return () => {};
  const previous = node.getAttribute(attribute);
  node.setAttribute(attribute, value);
  return () => {
    if (previous === null) node.removeAttribute(attribute);
    else node.setAttribute(attribute, previous);
  };
}

/**
 * @param title      the full `<title>`, including the brand.
 * @param path       the route's own path, e.g. `/tools/classcalc`.
 * @param description what a search result should say under the title.
 */
export function usePageMeta(title: string, path: string, description: string): void {
  useEffect(() => {
    const previousTitle = document.title;
    document.title = title;

    const restore = [
      setMeta('link[rel="canonical"]', 'href', SITE_ORIGIN + path),
      setMeta('meta[name="description"]', 'content', description),
      setMeta('meta[property="og:url"]', 'content', SITE_ORIGIN + path),
      setMeta('meta[property="og:title"]', 'content', title),
      setMeta('meta[property="og:description"]', 'content', description),
    ];

    return () => {
      document.title = previousTitle;
      restore.forEach((undo) => undo());
    };
  }, [title, path, description]);
}
